import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface VerificationCodeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (code: string) => void;
    isLoading?: boolean;
    type: "email" | "phone";
}

export const VerificationCodeDialog = ({
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    type,
}: VerificationCodeDialogProps) => {
    const { t } = useTranslation();
    const [code, setCode] = React.useState("");

    React.useEffect(() => {
        if (!isOpen) {
            setCode("");
        }
    }, [isOpen]);

    const handleComplete = (value: string) => {
        setCode(value);
    };

    const handleSubmit = () => {
        onSubmit(code);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {t("settings.verification.dialog.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t(`settings.verification.dialog.description.${type}`)}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center space-y-4">
                    <InputOTP
                        maxLength={6}
                        value={code}
                        onChange={(value) => setCode(value)}
                        onComplete={handleComplete}
                    >
                        <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                        </InputOTPGroup>
                    </InputOTP>
                    <Button
                        onClick={handleSubmit}
                        className="w-full"
                        disabled={code.length !== 6 || isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        {t("settings.verification.verify")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}; 